/**
 * PORT of `_reference/TC Visuals/Documentation/toast-module/src/deferred-ops.js`, verbatim
 * except two tagged divergences: (1) showUndoToast → showTransactionalUndoToast (D3);
 * (2) window._tcDeferredOpsBound → window._pbDeferredOpsBound (builder namespace).
 *
 * Deferred, reversible internal operations.
 *
 * The undo toast owns the only countdown timer. This module only tracks keyed
 * operations and turns the toast's commit/undo callbacks into a promise. Pending
 * work intentionally survives route/view teardown because the toast is portaled
 * to `document.body`; page lifecycle events commit it before the page disappears.
 */

import { showTransactionalUndoToast } from './toast.js'; // [builder-divergence 1] showUndoToast → showTransactionalUndoToast (D3)

const pending = new Map();
let lifecycleBound = false;

function onPageHide() {
  void flushDeferredOps();
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') void flushDeferredOps();
}

function bindLifecycleFlush() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window._pbDeferredOpsBound) return; // [builder-divergence 2] _tcDeferredOpsBound → _pbDeferredOpsBound
  window._pbDeferredOpsBound = true;
  lifecycleBound = true;
  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibilityChange);
}

/**
 * Schedule an operation behind a short Undo window.
 *
 * A second operation with the same key first commits the existing one, then
 * starts its own window; silently replacing a user's requested write would be
 * data loss. Callback errors reject the returned promise.
 *
 * @param {{
 *   key: string,
 *   message: string,
 *   seconds?: number,
 *   run: () => (void|Promise<void>|boolean|Promise<boolean>),
 *   onUndo?: () => (void|Promise<void>)
 * }} options
 * @returns {Promise<'committed'|'undone'>}
 */
export function runWithUndo({ key, message, seconds = 5, run, onUndo }) {
  const normalizedKey = String(key || '').trim();
  if (!normalizedKey) return Promise.reject(new TypeError('Deferred operation key is required'));
  if (typeof run !== 'function') {
    return Promise.reject(new TypeError('Deferred operation run callback is required'));
  }

  bindLifecycleFlush();

  const existing = pending.get(normalizedKey);
  if (existing) existing.toast.dismiss();

  let resolvePromise;
  let rejectPromise;
  const promise = new Promise((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });

  const entry = {
    key: normalizedKey,
    promise,
    toast: null,
    settling: false,
    suppressCallback: false
  };

  async function settle(result) {
    if (entry.settling) return;
    entry.settling = true;
    if (pending.get(normalizedKey) === entry) pending.delete(normalizedKey);
    try {
      if (!entry.suppressCallback) {
        if (result === 'committed') await run();
        else if (typeof onUndo === 'function') await onUndo();
      }
      resolvePromise(result);
    } catch (error) {
      rejectPromise(error);
    }
  }

  entry.toast = showTransactionalUndoToast(message, {
    seconds,
    onCommit: () => {
      void settle('committed');
    },
    onUndo: () => {
      void settle('undone');
    }
  });
  pending.set(normalizedKey, entry);
  return promise;
}

/** Commit every pending operation immediately. */
export function flushDeferredOps() {
  const entries = Array.from(pending.values());
  entries.forEach((entry) => entry.toast.dismiss());
  return Promise.allSettled(entries.map((entry) => entry.promise));
}

/** Follow the Undo path for one keyed operation. */
export function cancelDeferredOp(key) {
  const entry = pending.get(String(key || '').trim());
  if (!entry) return false;
  entry.toast.cancel();
  return true;
}

/** Snapshot the currently pending keys (oldest first). */
export function pendingDeferredOps() {
  return Array.from(pending.keys());
}

/** Test-only cleanup: clear timers/toasts without running product callbacks. */
export function __resetForTest() {
  Array.from(pending.values()).forEach((entry) => {
    entry.suppressCallback = true;
    entry.toast.cancel();
  });
  pending.clear();

  if (lifecycleBound && typeof window !== 'undefined' && typeof document !== 'undefined') {
    window.removeEventListener('pagehide', onPageHide);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window._pbDeferredOpsBound = false;
    lifecycleBound = false;
  }
}
